import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { objectMapToSelectOptions } from 'core/utils';
import { DEFAULT_SHIPPING_METHOD_NAME } from 'corporate/constants';
import { FreightTermFormField, ShippingMethodFormField } from 'corporate/elements';
import { ExternalCustomerNumberField } from 'customer/components';
import { ICustomerExternalNumberPayload } from 'customer/types';
import { CustomerContactFormFields } from 'quotes/components/CustomerQuoteUpsert/CustomerContactFormFields';
import { ShipSelectionDateFields } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields';
import { QuoteEntityType } from 'quotes/enums';
import { ICreateOrderForm, ICustomerQuote } from 'quotes/types';
import { getOrderTypeLabels } from 'quotes/utils';

interface IProps {
  quote: ICustomerQuote;
  isOpen?: boolean;
  isAlternateVersionEnabled: boolean;
  requiredFields?: boolean;
  externalCustomerNumberPayload?: ICustomerExternalNumberPayload;
  onClose?: (reset?: UseFormReset<ICreateOrderForm>) => void;
  onSubmit?: (reset?: UseFormReset<ICreateOrderForm>) => (data: ICreateOrderForm) => void;
}

export const QuotePartCreateCustomerOrderForm = ({
  quote,
  isOpen = false,
  isAlternateVersionEnabled = false,
  requiredFields = false,
  externalCustomerNumberPayload = {},
  onClose,
  onSubmit,
}: IProps) => {
  const { t } = useTranslation();

  const { defaultValues } = useFormContext();

  return (
    <Form
      title={t('titleCreateOrder')}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelCreateOrder')}
      cancelBtnLabel={t('buttonLabelCancel')}
      onSubmit={onSubmit}
    >
      {!isAlternateVersionEnabled && (
        <ExternalCustomerNumberField
          requestPayload={externalCustomerNumberPayload}
          required={requiredFields}
          enabled={isOpen}
          typeChoiceEnabled={!isAlternateVersionEnabled}
        />
      )}

      {isAlternateVersionEnabled && (
        <CustomerContactFormFields
          entityType={QuoteEntityType.ORDER}
          projectId={quote?.vendorProjectId}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          hiddenFields={{
            contact: true,
          }}
          initialSelectedContactGroup={quote?.customerContactGroup}
          enableOneTimeShipToAddressEdit
        />
      )}

      <FormField
        name="purchaseOrderNumber"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelPurchaseOrderNumber'),
          required: requiredFields,
        }}
      />

      {isAlternateVersionEnabled && (
        <>
          <FormField
            name="orderType"
            type={InputType.SELECT}
            inputProps={{
              label: t('labelOrderType'),
              required: requiredFields,
              options: objectMapToSelectOptions(getOrderTypeLabels()),
            }}
          />

          <ShipSelectionDateFields required={requiredFields} />

          <ShippingMethodFormField
            name="shippingMethod"
            defaultName={DEFAULT_SHIPPING_METHOD_NAME}
            initialValue={(defaultValues as ICreateOrderForm)?.shippingMethod}
            required={requiredFields}
          />

          <FreightTermFormField name="freightTerm" required={requiredFields} />
        </>
      )}
    </Form>
  );
};
